import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import FilterPanel from '@components/FilterPanel';
import I18n from '@utils/i18n';
import { KendoLocalizationProvider } from '@contexts/kendo-localization-context';
import useDebounce from '@hooks/useDebounce';
import { saveCacheToLocalStorage, loadCacheFromLocalStorage } from '@helpers/localStorage';
import { Text } from '@ui/atoms/Text';
import { WorkspaceHeader } from '@ui/molecules/Header/WorkspaceHeader';
import { Table, TableSize } from '@ui/molecules/Table';
import { ColumnVisibilityControl } from '@ui/molecules/Table/components/ColumnVisibility/ColumnVisibilityControl';

import TagsGridContext from './context';
import TagDrawer from './nested/TagDrawer';
import useFilters from './hooks/useFilters';
import { fetchTags } from './services/TagsService';
import { actionTypes, reducer } from './reducer';
import {
  ALL_COLUMNS,
  CACHE_KEY,
  INITIAL_FILTERS,
  INITIAL_PAGE,
  INITIAL_SORT,
  PAGE_SIZES,
  SORTABLE_COLUMNS,
  buildColumnVisibilityItems,
  buildColumns,
  t
} from './constants';

// IP-15330: listing of the metadata tags applied via acts_as_taggable.
//
// This component owns the whole screen, not just the table: Quimby
// WorkspaceHeader (Molecules/Header/WorkspaceHeader) for the page header, the
// Quimby detail Table (Molecules/Table, `variant="detail"`) for the listing, and
// a Brushaway (Molecules/Brushaway) for the tag detail that used to be its own
// page. The ERB template is a mount point and a flash container only.
//
// Server-side paging/sorting/filtering — Kendo::TagsDatatable computes the
// per-type counts as conditional aggregates, so the grid never holds more than
// one page and sorting by a type count is a database ORDER BY rather than a
// client-side sort over a partial set.
function TagsIndex({ taggableTypeLabels, canEdit, initialTagId, pageTitle, intro }) {
  const initialState = useMemo(() => {
    const cache = loadCacheFromLocalStorage(CACHE_KEY);

    // A cached shape from an older deploy can be missing keys this build expects,
    // which throws on first render. Validate before trusting it.
    const cacheIsValid = cache?.filters?.values && cache?.sort && cache?.page;

    return {
      data: [],
      totalCount: 0,
      loading: true,
      failed: false,
      page: cacheIsValid ? cache.page : INITIAL_PAGE,
      sort: cacheIsValid ? cache.sort : INITIAL_SORT,
      filters: cacheIsValid ? cache.filters : INITIAL_FILTERS,
      // Guard the cached column list: a name dropped in a later deploy would
      // otherwise be looked up as a column that no longer exists.
      columns: cacheIsValid && cache.columns?.every((c) => ALL_COLUMNS.includes(c)) ? cache.columns : ALL_COLUMNS
    };
  }, []);

  const [state, dispatch] = useReducer(reducer, initialState);
  const { page, sort, filters, columns: visibleColumnIds, data, totalCount, loading, failed } = state;

  // Bumped after any mutation so the listing re-reads the page it is on.
  const [reloadToken, setReloadToken] = useState(0);
  // Gated on canEdit, not just seeded from the prop: a non-admin hitting the
  // deep link /settings/tags/:id/edit would otherwise get the rename field,
  // the per-type detach buttons and delete. The server refuses all three, but
  // the access model for this screen is that non-admins see a read-only list.
  const [openTagId, setOpenTagId] = useState(canEdit ? initialTagId : null);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);

  const debouncedFilters = useDebounce(filters);
  const filtersList = useFilters({ filters, dispatch, taggableTypeLabels });

  useEffect(() => {
    saveCacheToLocalStorage(CACHE_KEY, { page, sort, filters, columns: visibleColumnIds });
  }, [page, sort, filters, visibleColumnIds]);

  useEffect(() => {
    if (filters !== debouncedFilters) return undefined;

    dispatch({ type: actionTypes.LOADING_CHANGED, loading: true });
    const controller = new AbortController();

    // eslint-disable-next-line no-void
    void (async () => {
      try {
        const payload = await fetchTags({ page, sort, filters: debouncedFilters, signal: controller.signal });
        dispatch({ type: actionTypes.DATA_LOADED, data: payload });
      } catch (error) {
        if (axios.isCancel(error)) return;
        dispatch({ type: actionTypes.LOAD_FAILED });
      }
    })();

    return () => controller.abort();
  }, [page, sort, filters, debouncedFilters, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  const onResetFilters = () => dispatch({ type: actionTypes.FILTERS_RESET, filters: INITIAL_FILTERS });

  const onResetCache = () => {
    if (!window.confirm(I18n.t('generic.are_you_sure'))) return;
    localStorage.removeItem(CACHE_KEY);
    window.location.reload();
  };

  // Non-admins get a read-only listing: no row action column.
  const visibleIds = useMemo(
    () => (canEdit ? visibleColumnIds : visibleColumnIds.filter((id) => id !== 'actions')),
    [visibleColumnIds, canEdit]
  );
  const configurableIds = useMemo(
    () => (canEdit ? ALL_COLUMNS : ALL_COLUMNS.filter((id) => id !== 'actions')),
    [canEdit]
  );

  const columns = useMemo(() => buildColumns(visibleIds), [visibleIds]);
  const visibilityItems = useMemo(() => buildColumnVisibilityItems(configurableIds), [configurableIds]);

  const contextValue = useMemo(() => ({ canEdit, onOpenTag: setOpenTagId }), [canEdit]);

  return (
    <KendoLocalizationProvider>
      <TagsGridContext.Provider value={contextValue}>
        {/* Quimby WorkspaceHeader — title only / no divider, as on the other
            settings screens; the intro copy provides the separation. */}
        <WorkspaceHeader title={pageTitle} divider={false} />

        <section className="workspace__section">
          {/* 14px body copy, held to 700px per Product. `body` is the 14px Quimby
              variant and ships a 720px cap, so only the width is overridden. */}
          <Text variant="body" as="p" style={{ maxWidth: '700px' }}>
            {intro}
          </Text>
        </section>

        <FilterPanel onResetFilters={onResetFilters} onResetCache={onResetCache} filters={filtersList} />

        {/* Shared column-visibility popup, opened from the per-header 3-dot triggers. */}
        <ColumnVisibilityControl
          columns={visibilityItems}
          visibleColumnKeys={visibleIds}
          defaultColumnKeys={configurableIds}
          onApply={(keys) => dispatch({ type: actionTypes.COLUMNS_CHANGED, columns: keys })}
          open={columnMenuAnchor !== null}
          anchor={columnMenuAnchor}
          onOpenChange={(open) => {
            if (!open) setColumnMenuAnchor(null);
          }}
          labels={{
            buttonAriaLabel: t('columns.button'),
            searchPlaceholder: t('columns.search'),
            apply: t('columns.apply'),
            restoreDefaults: t('columns.restore_defaults'),
            noResults: t('columns.no_results')
          }}
        />

        <Table
          data={data}
          columns={columns}
          variant="detail"
          size={TableSize.Full}
          getItemId={(row) => row.id}
          sortable
          sortableColumns={SORTABLE_COLUMNS}
          onSort={(field, direction) =>
            dispatch({ type: actionTypes.SORT_CHANGED, sort: direction ? [{ field, dir: direction }] : [] })
          }
          sortBy={sort[0]?.field ?? null}
          sortDirection={sort[0]?.dir ?? null}
          onPageChange={(nextPage) => dispatch({ type: actionTypes.PAGE_CHANGED, page: nextPage })}
          serverCurrentPage={Math.floor(page.skip / page.take) + 1}
          serverTotalPages={Math.max(1, Math.ceil(totalCount / page.take))}
          serverTotalItems={totalCount}
          pageSize={page.take}
          pageSizeOptions={PAGE_SIZES}
          onPageSizeChange={(pageSize) => dispatch({ type: actionTypes.PAGE_SIZE_CHANGED, pageSize })}
          onColumnMenuOpen={(_dataKey, anchor) => setColumnMenuAnchor(anchor)}
          isLoading={loading}
          loadingLabel={I18n.t('generic.loading')}
          isError={failed}
          errorContent={t('table.error')}
          emptyContent={t('table.empty')}
          paginationLabels={{
            page: t('table.pagination.page'),
            of: t('table.pagination.of'),
            itemsPerPage: t('table.pagination.items_per_page')
          }}
        />

        {canEdit && (
          <TagDrawer
            tagId={openTagId}
            taggableTypeLabels={taggableTypeLabels}
            onMutated={reload}
            onClose={() => setOpenTagId(null)}
          />
        )}
      </TagsGridContext.Provider>
    </KendoLocalizationProvider>
  );
}

TagsIndex.propTypes = {
  taggableTypeLabels: PropTypes.objectOf(PropTypes.string).isRequired,
  pageTitle: PropTypes.string.isRequired,
  intro: PropTypes.string.isRequired,
  canEdit: PropTypes.bool,
  initialTagId: PropTypes.number
};

TagsIndex.defaultProps = {
  canEdit: false,
  initialTagId: null
};

export default TagsIndex;
